import React, { useState, useEffect } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Ticket, ChevronRight, CheckCircle, Banknote, CreditCard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RafflePage() {
  usePageTitle('Raffle');
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [quantity, setQuantity] = useState(1);
  
  // Purchase result
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [nextNumber, setNextNumber] = useState(1);
  
  // Check URL for payment success
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const entryId = urlParams.get('entryId');
    
    if (payment === 'success' && entryId) {
      // Confirm payment
      axios.post(`${API}/raffle/confirm-payment`, { entryId })
        .then(() => {
          toast.success('Payment confirmed! SMS sent.');
          window.history.replaceState({}, '', '/raffle');
          setStep(4); // Show success
        })
        .catch(err => {
          console.error(err);
          toast.error('Payment confirmation failed');
        });
    }
  }, []);
  
  // Load raffle status
  useEffect(() => {
    axios.get(`${API}/raffle/status`)
      .then(res => {
        setNextNumber(res.data.nextNumber);
      })
      .catch(err => console.error(err));
  }, []);
  
  const calculatePrice = (qty) => {
    const deals = Math.floor(qty / 15);
    const remaining = qty % 15;
    const strips = Math.floor(remaining / 5);
    const singles = remaining % 5;
    return deals * 10 + strips * 5 + singles;
  };
  
  const price = calculatePrice(quantity);
  
  const handleNext = () => {
    if (step === 1) {
      if (!firstName.trim() || !lastName.trim()) {
        toast.error('Please enter your name');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!phone.trim() || phone.length < 10) {
        toast.error('Please enter a valid phone number');
        return;
      }
      setStep(3);
    }
  };
  
  const handlePurchase = async (paymentMethod) => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/raffle/purchase`, {
        firstName,
        lastName,
        phone,
        quantity,
        paymentMethod
      });
      
      if (response.data.success) {
        setPurchaseResult(response.data);
        
        if (paymentMethod === 'cash') {
          toast.success('Purchase complete! SMS sent.');
          setStep(4);
        } else if (response.data.checkoutUrl) {
          // Redirect to SumUp
          window.location.href = response.data.checkoutUrl;
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handleNewSale = () => {
    setFirstName('');
    setLastName('');
    setPhone('');
    setQuantity(1);
    setPurchaseResult(null);
    setStep(1);
    
    // Refresh next number
    axios.get(`${API}/raffle/status`)
      .then(res => setNextNumber(res.data.nextNumber))
      .catch(err => console.error(err));
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center border-b">
          <div className="flex items-center justify-center mb-2">
            <Ticket className="w-10 h-10 text-purple-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Raffle Tickets</CardTitle>
          <p className="text-sm text-gray-500">Next number: #{nextNumber}</p>
        </CardHeader>
        
        <CardContent className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 font-bold mb-2">
                  1
                </div>
                <h3 className="text-lg font-semibold">Enter Your Name</h3>
              </div>
              
              <div>
                <Label>First Name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  className="text-lg"
                />
              </div>
              
              <div>
                <Label>Last Name</Label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Smith"
                  className="text-lg"
                />
              </div>
              
              <Button onClick={handleNext} className="w-full bg-purple-600 hover:bg-purple-700" size="lg">
                Next <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
          
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 font-bold mb-2">
                  2
                </div>
                <h3 className="text-lg font-semibold">Phone Number</h3>
                <p className="text-sm text-gray-500">For SMS confirmation</p>
              </div>
              
              <div>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="07123456789"
                  className="text-lg"
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={() => setStep(1)} variant="outline" className="w-full">
                  Back
                </Button>
                <Button onClick={handleNext} className="w-full bg-purple-600 hover:bg-purple-700">
                  Next <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}
          
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-600 font-bold mb-2">
                  3
                </div>
                <h3 className="text-lg font-semibold">Select Quantity</h3>
              </div>
              
              <div>
                <Label>Number of Tickets</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="text-center text-xl font-bold"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </Button>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Quantity:</span>
                  <span className="font-bold">{quantity} ticket{quantity !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Price:</span>
                  <span className="text-2xl font-bold text-purple-600">£{price}</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Best value: 15 numbers for £10
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-base font-semibold">Payment Method:</Label>
                
                <Button
                  onClick={() => handlePurchase('cash')}
                  disabled={loading}
                  className="w-full bg-green-600 hover:bg-green-700 h-14"
                  size="lg"
                >
                  <Banknote className="w-5 h-5 mr-2" />
                  CASH - Mark as Paid
                </Button>
                
                <Button
                  onClick={() => handlePurchase('card')}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 h-14"
                  size="lg"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  CARD - Pay with SumUp
                </Button>
              </div>
              
              <Button onClick={() => setStep(2)} variant="outline" className="w-full">
                Back
              </Button>
            </div>
          )}
          
          {step === 4 && purchaseResult && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>
              
              <h3 className="text-2xl font-bold">Purchase Complete!</h3>
              
              <div className="bg-gray-50 p-4 rounded-lg text-left">
                <div className="mb-2">
                  <span className="text-sm text-gray-600">Name:</span>
                  <p className="font-semibold">{firstName} {lastName}</p>
                </div>
                <div className="mb-2">
                  <span className="text-sm text-gray-600">Phone:</span>
                  <p className="font-semibold">{phone}</p>
                </div>
                <div className="mb-2">
                  <span className="text-sm text-gray-600">Numbers:</span>
                  <p className="font-bold text-purple-600">
                    {purchaseResult.numbers.join(', ')}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Price Paid:</span>
                  <p className="text-xl font-bold">£{purchaseResult.price}</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                SMS confirmation sent to {phone}
              </p>
              
              <Button onClick={handleNewSale} className="w-full bg-purple-600 hover:bg-purple-700" size="lg">
                New Sale
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
